import { create } from "zustand"

/**
 * @typedef {Object} Presence.Tab
 * @property {string} page - The page pathname (e.g., '/broadcasts')
 * @property {number} timestamp - When the tab was opened
 * @property {string} status - Tab status (e.g., 'active')
 * @property {number} lastSeen - Last activity timestamp
 * @property {string} title - Document title
 * @property {boolean} isActive - Whether the tab is currently visible/focused
 */

/**
 * @typedef {Object} Presence.User
 * @property {number} id - User ID
 * @property {string} name - User name
 * @property {string} email - User email
 * @property {string} avatarUrl - User avatar URL
 * @property {number} accountId - Account ID
 * @property {Object.<string, Presence.Tab>} tabs - Object mapping session IDs to tab information
 */

/**
 * @typedef {Object} Presence.UserOnPage
 * @property {number} id - User ID
 * @property {string} name - User name
 * @property {string} email - User email
 * @property {string} avatarUrl - User avatar URL
 * @property {number} accountId - Account ID
 * @property {Object.<string, Presence.Tab>} tabs - Object mapping session IDs to tab information
 * @property {string} sessionId - The session ID for this specific tab
 * @property {Presence.Tab} tabInfo - The tab information for this specific tab
 */

/**
 * @typedef {Object} PresenceState
 * @property {Presence.User[]} onlineUsers - Array of online users with their tabs
 * @property {HocuspocusProvider|null} provider - The Hocuspocus provider instance
 * @property {boolean} isConnected - Whether the provider is connected
 * @property {string|null} sessionId - The current session ID
 */

/**
 * Zustand store for managing presence state across the application.
 * Tracks online users, their active tabs, and WebSocket connection status.
 *
 * @type {import('zustand').UseBoundStore<import('zustand').StoreApi<PresenceState>>}
 * @returns {PresenceState} The presence store state
 */
const usePresenceStore = create(() => ({
  onlineUsers: [],
  provider: null,
  isConnected: false,
  sessionId: null
}))

/**
 * Sets the online users in the store
 * @param {Presence.User[]} onlineUsers - Array of online users with their tabs
 */
export const setOnlineUsers = onlineUsers => {
  usePresenceStore.setState({ onlineUsers })
}

/**
 * Sets the session ID in the store
 * @param {string} sessionId - The session ID to set
 */
export const setSessionId = sessionId => {
  usePresenceStore.setState({ sessionId })
}

/**
 * Hook to get all online users
 * @returns {Presence.User[]} Array of online users with their tabs
 */
export const useOnlineUsers = () => {
  return usePresenceStore(state => state.onlineUsers)
}

/**
 * Hook to get the current session ID
 * @returns {string|null} The current session ID
 */
export const useSessionId = () => {
  return usePresenceStore(state => state.sessionId)
}

/**
 * Hook to get online users on a specific page
 * @param {string} page - The page pathname to filter users by
 * @returns {Presence.UserOnPage[]} Array of users on the specified page with tab info
 */
export const useOnlineUsersOnPage = page => {
  return usePresenceStore(state => {
    if (!page) return []

    return filterUsersOnPage(state.onlineUsers, page)
  })
}

/**
 * Returns online users on a specific page
 * @param {string} page - The page pathname to filter users by
 * @returns {Presence.UserOnPage[]} Array of users on the specified page with tab info
 */
export const getOnlineUsersOnPage = page => {
  const state = usePresenceStore.getState()
  if (!page) return []

  return filterUsersOnPage(state.onlineUsers, page)
}

function filterUsersOnPage(onlineUsers, page) {
  const usersOnPage = []

  onlineUsers.forEach(user => {
    Object.entries(user.tabs).forEach(([sessionId, tab]) => {
      if (tab.page === page) {
        // Create a user entry for each tab on this page
        usersOnPage.push({
          ...user,
          sessionId,
          tabInfo: tab
        })
      }
    })
  })
  return usersOnPage
}

/**
 * Sets the Hocuspocus provider in the store
 * @param {HocuspocusProvider} provider - The Hocuspocus provider instance
 */
export const setProvider = provider => {
  usePresenceStore.setState({ provider })
}

/**
 * Sets the connection status in the store
 * @param {boolean} isConnected - Whether the provider is connected
 */
export const setIsConnected = isConnected => {
  usePresenceStore.setState({ isConnected })
}

/**
 * Hook to get the Hocuspocus provider
 * @returns {HocuspocusProvider|null} The provider instance or null if not yet available
 */
export const usePresenceProvider = () => {
  return usePresenceStore(state => state.provider)
}

/**
 * Hook to get the connection status
 * @returns {boolean} Whether the provider is connected
 */
export const useIsConnected = () => {
  return usePresenceStore(state => state.isConnected)
}
